import React, { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { FaHospitalAlt, FaUserMd, FaHeartbeat, FaShieldAlt, FaChevronUp } from 'react-icons/fa';
import PatientForm from './components/PatientForm';
import MedicationTable from './components/MedicationTable';
import ResultsDashboard from './components/ResultsDashboard';
import LoadingSpinner from './components/LoadingSpinner';
import DeprescribingCockpit from './components/DeprescribingCockpit';
import LandingPage from './components/LandingPage';
import { AnimatePresence, motion } from 'framer-motion';
import { analyzePatient, warmupBackend } from './services/api';
import './App.css';



function AppContent() {
  const [patientData, setPatientData] = useState(null);
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [viewMode, setViewMode] = useState('standard');
  const [backendState, setBackendState] = useState('idle');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePatientSubmit = async (formData) => {
    setIsLoading(true);
    setError(null);
    setShowResults(false);

    try {
      const analysisResults = await analyzePatient(formData);
      setPatientData(formData);
      setResults(analysisResults);
      setShowResults(true);
      setTimeout(() => {
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
      }, 100);
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'object'
        ? JSON.stringify(errorDetail, null, 2)
        : (errorDetail || err.message || 'An error occurred during analysis');
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setPatientData(null);
    setResults(null);
    setShowResults(false);
    setError(null);
    setViewMode('standard');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStartBackend = async () => {
    if (backendState === 'starting' || backendState === 'ready') return;
    setBackendState('starting');
    try {
      await warmupBackend();
      setBackendState('ready');
    } catch (e) {
      setBackendState('error');
    }
  };

  const handleOpenCockpit = () => {
    if (!patientData || !results) return;
    setViewMode('cockpit');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCloseCockpit = () => {
    setViewMode('standard');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isCockpit = viewMode === 'cockpit' && patientData && results;

  return (
    <div className="min-h-screen bg-neo-bg">
      <header className={`bg-white border-b-4 border-neo-main sticky top-0 z-50 ${isCockpit ? 'hidden' : ''}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-neo-primary rounded-lg flex items-center justify-center border-2 border-neo-main shadow-neo-brutal">
                <FaHospitalAlt className="text-2xl text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-black tracking-tighter flex items-center gap-2 text-neo-main">
                  <span>DEPRESCRIBING</span>
                  <span className="text-neo-primary px-1 bg-neo-bg border-2 border-neo-main rounded shadow-neo-sm text-lg">CDS</span>
                </h1>
                <p className="text-neo-text-sub text-xs font-bold uppercase tracking-wider flex items-center gap-2">
                  <FaShieldAlt className="text-xs" />
                  Evidence-Based Medication Review
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="hidden md:flex items-center gap-4 text-sm font-bold text-neo-main">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 border-neo-main bg-neo-bg shadow-neo-sm">
                  <FaUserMd className="text-neo-primary" />
                  <span>9-Module Analysis</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border-2 border-neo-main bg-neo-bg shadow-neo-sm">
                  <FaHeartbeat className="text-neo-danger" />
                  <span>Clinical Grade</span>
                </div>
              </div>

              <div className="hidden md:flex items-center gap-4">
                <button
                  type="button"
                  onClick={handleStartBackend}
                  disabled={backendState === 'starting'}
                  className={`px-4 py-2 rounded-lg font-black border-2 border-neo-main shadow-neo-brutal transition-all duration-200 flex items-center gap-2 text-sm uppercase tracking-wide ${
                    backendState === 'ready'
                      ? 'bg-neo-success text-white'
                      : backendState === 'error'
                        ? 'bg-neo-danger text-white'
                        : 'bg-neo-main text-white'
                  } ${backendState === 'starting' ? 'opacity-75 cursor-not-allowed' : 'hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-neo-sm'}`}
                >
                  {backendState === 'ready'
                    ? 'Backend Ready'
                    : backendState === 'starting'
                      ? 'Starting Backend...'
                      : backendState === 'error'
                        ? 'Retry Backend'
                        : 'Start Backend'}
                </button>

                {showResults && (
                  <button
                    onClick={handleReset}
                    className="px-5 py-2 bg-neo-warning text-neo-main rounded-lg font-black border-2 border-neo-main shadow-neo-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-neo-sm transition-all duration-200 flex items-center gap-2 text-sm"
                  >
                    NEW PATIENT
                  </button>
                )}
              </div>

              <button
                type="button"
                className="md:hidden w-11 h-11 bg-white border-2 border-neo-main rounded-lg shadow-neo-brutal flex items-center justify-center"
                onClick={() => setMobileMenuOpen((v) => !v)}
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  {mobileMenuOpen ? (
                    <>
                      <path d="M6 6l12 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                      <path d="M18 6L6 18" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                    </>
                  ) : (
                    <>
                      <path d="M4 6h16" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                      <path d="M4 12h16" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                      <path d="M4 18h16" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                    </>
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-[60]">
          <div
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="absolute top-[76px] left-4 right-4 bg-white border-2 border-neo-main rounded-xl shadow-neo-brutal p-4 space-y-3">
            <button
              type="button"
              onClick={async () => {
                await handleStartBackend();
                setMobileMenuOpen(false);
              }}
              disabled={backendState === 'starting'}
              className={`w-full px-4 py-3 rounded-lg font-black border-2 border-neo-main shadow-neo-brutal transition-all duration-200 text-sm uppercase tracking-wide ${
                backendState === 'ready'
                  ? 'bg-neo-success text-white'
                  : backendState === 'error'
                    ? 'bg-neo-danger text-white'
                    : 'bg-neo-main text-white'
              } ${backendState === 'starting' ? 'opacity-75 cursor-not-allowed' : ''}`}
            >
              {backendState === 'ready'
                ? 'Backend Ready'
                : backendState === 'starting'
                  ? 'Starting Backend...'
                  : backendState === 'error'
                    ? 'Retry Backend'
                    : 'Start Backend'}
            </button>

            {showResults && (
              <button
                type="button"
                onClick={() => {
                  handleReset();
                  setMobileMenuOpen(false);
                }}
                className="w-full px-4 py-3 bg-neo-warning text-neo-main rounded-lg font-black border-2 border-neo-main shadow-neo-brutal text-sm uppercase tracking-wide"
              >
                New Patient
              </button>
            )}
          </div>
        </div>
      )}

      <main className={isCockpit ? '' : 'container mx-auto px-4 py-8'}>
        <AnimatePresence mode="wait">
          <motion.div
            key={isCockpit ? 'cockpit' : 'standard'}
            initial={{ opacity: 0, x: isCockpit ? 40 : -40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: isCockpit ? -40 : 40 }}
            transition={{ duration: 0.4, ease: 'circOut' }}
          >
            {!isCockpit && !showResults && !isLoading && (
              <div className="animate-fade-in-up">
                <div className="text-center mb-10">
                  <div className="inline-flex items-center gap-2 bg-neo-bg px-4 py-2 rounded-lg border-2 border-neo-main shadow-neo-sm mb-6">
                    <span className="w-3 h-3 bg-neo-success rounded-full border-2 border-neo-main animate-pulse"></span>
                    <span className="text-xs font-black uppercase tracking-widest text-neo-main">System Ready</span>
                  </div>
                  <h2 className="text-4xl md:text-5xl font-black text-neo-main mb-4 tracking-tight">
                    PATIENT MEDICATION ANALYSIS
                  </h2>
                  <p className="text-neo-text-sub font-medium max-w-2xl mx-auto text-lg">
                    Enter patient information below to receive comprehensive deprescribing recommendations
                    powered by evidence-based clinical decision support algorithms.
                  </p>
                </div>
                <PatientForm onSubmit={handlePatientSubmit} isLoading={isLoading} />
              </div>
            )}

            {!isCockpit && isLoading && (
              <div className="max-w-4xl mx-auto">
                <div className="bg-white border-2 border-neo-main rounded-xl p-12 text-center shadow-neo-brutal">
                  <LoadingSpinner message="ANALYZING PATIENT MEDICATIONS THROUGH 9 CLINICAL MODULES..." showAI={true} />
                  <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-lg mx-auto">
                    {['STOPP/START', 'Beers Criteria', 'ACB Score'].map((module) => (
                      <div key={module} className="bg-neo-bg rounded-lg p-3 border-2 border-neo-main shadow-neo-sm">
                        <p className="text-xs font-black uppercase tracking-wider text-neo-main">{module}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {!isCockpit && error && (
              <div className="animate-fade-in max-w-4xl mx-auto">
                <div className="bg-white border-2 border-neo-main p-6 rounded-xl shadow-neo-brutal mb-8 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-2 h-full bg-neo-danger"></div>
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 bg-neo-danger rounded-lg flex items-center justify-center flex-shrink-0 border-2 border-neo-main shadow-neo-sm">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-black text-neo-main mb-2 uppercase">Analysis Error</h3>
                      <div className="text-neo-text font-medium mb-4 whitespace-pre-wrap font-mono text-sm bg-neo-bg p-3 rounded border-2 border-neo-main">
                        {error}
                      </div>
                      <button
                        onClick={handleReset}
                        className="px-6 py-2.5 bg-neo-danger text-white rounded-lg font-black border-2 border-neo-main shadow-neo-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-neo-sm transition-all duration-200 uppercase tracking-wide"
                      >
                        Try Again
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!isCockpit && showResults && patientData && (
              <div className="space-y-10 animate-fade-in-up">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center gap-2 bg-neo-bg px-4 py-2 rounded-lg border-2 border-neo-main shadow-neo-sm mb-4">
                    <svg className="w-4 h-4 text-neo-success" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm font-black text-neo-success uppercase tracking-wider">Analysis Complete</span>
                  </div>
                  <h2 className="text-3xl font-black text-neo-main uppercase tracking-tight">
                    Clinical Analysis Results
                  </h2>
                </div>
                <MedicationTable medications={patientData.medications} herbs={patientData.herbs} />
                <ResultsDashboard results={results} patientData={patientData} onOpenCockpit={handleOpenCockpit} />
              </div>
            )}

            {isCockpit && (
              <DeprescribingCockpit patientData={patientData} results={results} onBack={handleCloseCockpit} />
            )}
          </motion.div>
        </AnimatePresence>
      </main>

      <footer className="bg-neo-main text-white py-12 border-t-4 border-black mt-auto">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-neo-primary rounded-lg flex items-center justify-center border-2 border-white shadow-[4px_4px_0px_#ffffff]">
                <FaHospitalAlt className="text-3xl text-white" />
              </div>
              <div>
                <p className="font-black text-xl uppercase tracking-wider">Deprescribing Engine</p>
                <p className="text-gray-400 text-sm font-bold">v1.0 • 9-Module Clinical Analysis System</p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2 text-gray-300 font-bold text-sm">
                <FaShieldAlt className="text-neo-primary" />
                <span>HIPAA Compliant Design</span>
              </div>
              <div className="hidden md:block h-8 w-1 bg-gray-700"></div>
              <div className="text-gray-400 text-sm text-center md:text-right font-medium">
                For clinical decision support purposes only.<br className="hidden md:block" />
                Always consult with healthcare professionals.
              </div>
            </div>
          </div>
        </div>
      </footer>

      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 w-14 h-14 bg-neo-primary text-white rounded-lg border-2 border-neo-main shadow-neo-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-neo-sm transform transition-all duration-200 flex items-center justify-center z-50"
        >
          <FaChevronUp className="text-xl" />
        </button>
      )}
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/app" element={<AppContent />} />
    </Routes>
  );
}

export default App;